/**
 * Canonical vocabularies and the state rules the prototype learned the hard way.
 * Pure. No I/O. Every rule cites its provenance in PROJECT-BRIEF.md or the v3 plan.
 */
export const ACCOUNT_STATUSES = [
    'pending',
    'active',
    'trailing',
    'cancellation_requested',
    'closed',
    'collections',
    'unknown',
];
export const STATUS_SOURCES = ['manual', 'webhook', 'poll', 'email', 'portal_import'];
/** Higher wins. manual > webhook (re-fetched) > poll > email > portal_import. */
const SOURCE_RANK = {
    manual: 5,
    webhook: 4,
    poll: 3,
    email: 2,
    portal_import: 1,
};
/**
 * Decide whether an incoming status claim may replace the current one.
 * Rules (v3 plan, "Canonical status and the raw-to-canonical map"):
 *  - `closed` and `collections` are sticky against any non-manual downgrade.
 *  - `cancellation_requested` is sticky against `active` and `trailing` from poll/email/import,
 *    but yields to `closed` (the portal's "closed still applies over it").
 *  - A lower-trust source never overrides a higher-trust source's terminal status.
 *  - Same status from any source is a no-op (returns false so callers do not emit an event).
 */
export function shouldApplyStatus(current, incoming) {
    if (current === null)
        return true;
    if (current.status === incoming.status)
        return false;
    if (incoming.source === 'manual')
        return true;
    const sticky = ['closed', 'collections'];
    if (sticky.includes(current.status))
        return false;
    if (current.status === 'cancellation_requested') {
        return incoming.status === 'closed' || incoming.status === 'collections';
    }
    // An `unknown` from a lower-trust source never clobbers a known status.
    if (incoming.status === 'unknown' && SOURCE_RANK[incoming.source] < SOURCE_RANK[current.source]) {
        return false;
    }
    return true;
}
/** Maverick `processing.status` to canonical. Anything unmapped is `unknown` and goes to review. */
export function maverickStatusToCanonical(raw) {
    switch ((raw ?? '').trim().toLowerCase()) {
        case 'approved':
            return 'pending';
        case 'live':
            return 'active';
        case 'inactive':
            return 'trailing';
        case 'closed':
            return 'closed';
        default:
            return 'unknown';
    }
}
/** ISO360 `status` / `status_code` to canonical. Only ACTIVE/700 and PENDING/300 are documented. */
export function iso360StatusToCanonical(status, code) {
    const s = (status ?? '').trim().toUpperCase();
    const c = (code ?? '').trim();
    if (s === 'ACTIVE' || c === '700')
        return 'active';
    if (s === 'PENDING' || c === '300')
        return 'pending';
    return 'unknown';
}
/** EPI inbound email kinds (portal `epi_inbound_log.parsed_kind`) to canonical, for the one-time seed only. */
export function epiEmailKindToCanonical(kind) {
    switch ((kind ?? '').trim().toLowerCase()) {
        case 'approval':
            return 'pending';
        case 'account_activated':
            return 'active';
        case 'cancellation_request':
            return 'cancellation_requested';
        case 'recovery_placement':
            return 'collections';
        default:
            return 'unknown';
    }
}
// ---------------------------------------------------------------------------
// Tickets
// ---------------------------------------------------------------------------
export const TICKET_STATUSES = ['open', 'in_progress', 'waiting_on_merchant', 'stuck', 'resolved', 'closed'];
export const TICKET_SOURCES = ['phone', 'sms', 'email', 'internal', 'slack_bot', 'slack_import'];
export const PRIORITIES = ['low', 'normal', 'high', 'urgent'];
export const ROUTED_TO = ['EK', 'Maverick', 'EPI', 'Green Payments', 'Onboarding', 'RMA', 'Dev'];
export const WAITING_ON = ['client', 'team_check', 'approval', 'processor', 'vendor_shipping'];
export const BOARD_STAGES = ['new', 'in_progress', 'waiting', 'stuck', 'done'];
/**
 * Derived, never stored (prototype `lib/tickets/stage.ts`).
 *   resolved/closed -> done; stuck -> stuck; routed_to set -> waiting;
 *   waiting_on_merchant -> waiting; in_progress -> in_progress; default new.
 */
export function boardStage(status, routedTo) {
    if (status === 'resolved' || status === 'closed')
        return 'done';
    if (status === 'stuck')
        return 'stuck';
    if (routedTo)
        return 'waiting';
    if (status === 'waiting_on_merchant')
        return 'waiting';
    if (status === 'in_progress')
        return 'in_progress';
    return 'new';
}
/** `client`, `processor`, `vendor_shipping` pause SLA-aging visuals. */
export function agingPaused(waitingOn) {
    return waitingOn === 'client' || waitingOn === 'processor' || waitingOn === 'vendor_shipping';
}
/** Whole hours since creation, never negative. */
export function ticketAgeHours(createdAt, now = new Date()) {
    const t = typeof createdAt === 'string' ? new Date(createdAt).getTime() : createdAt.getTime();
    return Math.max(0, Math.floor((now.getTime() - t) / 3_600_000));
}
export const TERMINAL_TICKET_STATUSES = ['resolved', 'closed'];
// The close validator lives in close-validate.ts (ported 2026-10-13 with the root-cause vocabulary).
/** Reopen (terminal -> earlier status) clears resolution, root_cause, close_reason, resolved_at, closed_at. `applied_at` is never cleared. */
export function isReopen(from, to) {
    return TERMINAL_TICKET_STATUSES.includes(from) && !TERMINAL_TICKET_STATUSES.includes(to);
}
//# sourceMappingURL=status.js.map